import React, { Component, ErrorInfo, ReactNode } from "react";
import { store } from "../state/store";
import { navigateToView } from "../state/slices/navigationSlice";

interface Props {
    children?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
    showDetails: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null,
        showDetails: false,
    };

    public static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI.
        return { hasError: true, error, errorInfo: null, showDetails: false };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Uncaught error:", error, errorInfo);
        this.setState({ errorInfo });
    }

    private handleReload = () => {
        window.location.reload();
    };

    private handleGoBack = () => {
        const state = store.getState();
        const previousView = state.navigation.previousView;

        if (previousView) {
            store.dispatch(navigateToView({ view: previousView, isBack: true }));
        } else {
            // Fallback to dashboard if no previous view
            store.dispatch(navigateToView({ view: "dashboard" }));
        }

        // Reset error state to allow re-rendering the new view
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            showDetails: false,
        });
    };

    private toggleDetails = () => {
        this.setState((prevState) => ({ showDetails: !prevState.showDetails }));
    };

    public render() {
        if (this.state.hasError) {
            return (
                <div className="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
                    <div className="mb-6 bg-red-100 dark:bg-red-900/30 p-4 rounded-full shadow-inner animate-pulse">
                        <svg
                            className="w-16 h-16 text-red-500 dark:text-red-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            ></path>
                        </svg>
                    </div>
                    <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-4 tracking-tight">
                        Something went wrong
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-lg text-center text-lg leading-relaxed">
                        We encountered an unexpected error. You can try reloading the page, or go back to the previous screen.
                    </p>

                    <div className="flex flex-wrap gap-4 mb-8 justify-center">
                        <button
                            onClick={this.handleGoBack}
                            className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-all shadow-md active:scale-95 flex items-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Go Back
                        </button>
                        <button
                            onClick={this.handleReload}
                            className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-all shadow-lg shadow-primary-500/30 active:scale-95 flex items-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Reload Page
                        </button>
                        <button
                            onClick={this.toggleDetails}
                            className="px-6 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium rounded-lg border border-gray-300 dark:border-gray-600 transition-colors active:scale-95"
                        >
                            {this.state.showDetails ? "Hide Details" : "More Info"}
                        </button>
                    </div>

                    {this.state.showDetails && this.state.error && (
                        <div className="w-full max-w-3xl text-left animate-fade-in-up">
                            <div className="bg-gray-800 text-gray-200 p-6 rounded-lg overflow-x-auto text-sm font-mono whitespace-pre-wrap border border-gray-700 shadow-2xl">
                                <div className="mb-4 text-red-300 font-bold border-b border-gray-700 pb-2 text-base">
                                    {this.state.error.toString()}
                                </div>
                                <div className="text-gray-400 italic">
                                    Check the browser console for full stack trace.
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        return this.props.children;
    }
}
